import type { DsdEditorParsedItem } from "@/utils/dsd-editor/document-items";
import {
  getDsdEditorSumMismatchCells,
  getDsdEditorSumValidationCells,
  isDsdEditorSumMismatch,
} from "@/utils/dsd-editor/sum-validation";
import { useCallback, useEffect, useMemo, useState } from "react";
import { isTypingTarget } from "../is-typing-target";

/**
 * 합계 검증 탭의 선택 상태.
 * 이전/다음(←/→ 포함)은 불일치 셀만 돈다 — 패널의 xx/yy 가 불일치 셀 기준이다.
 */
export const useDsdReviewSumValidation = ({
  enabled,
  displayItems,
  requestCellScroll,
  onSelect,
}: {
  enabled: boolean;
  displayItems: DsdEditorParsedItem[];
  requestCellScroll: (cellId: string) => void;
  /** 합계 셀을 고를 때마다 — 다른 선택 하이라이트를 지우는 등 */
  onSelect?: (cellId: string) => void;
}) => {
  const cells = useMemo(
    () => getDsdEditorSumValidationCells(displayItems),
    [displayItems],
  );
  const mismatchCells = useMemo(
    () => getDsdEditorSumMismatchCells(cells),
    [cells],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // 문서가 다시 불러와져 셀이 사라지면 선택도 없는 것으로 본다
  const selectedCell = useMemo(
    () => cells.find((cell) => cell.cellId === selectedId) ?? null,
    [cells, selectedId],
  );
  const selectedCellId = selectedCell?.cellId ?? null;
  const highlightedCellIds = useMemo(
    () => new Set(selectedCell?.children.map((child) => child.cellId) ?? []),
    [selectedCell],
  );

  useEffect(() => {
    if (!enabled) setSelectedId(null);
  }, [enabled]);

  const select = useCallback(
    (cellId: string) => {
      onSelect?.(cellId);
      setSelectedId(cellId);
      requestCellScroll(cellId);
    },
    [onSelect, requestCellScroll],
  );
  const clear = useCallback(() => setSelectedId(null), []);

  const navigate = useCallback(
    (direction: -1 | 1) => {
      if (mismatchCells.length === 0) return;
      const index = mismatchCells.findIndex(
        (cell) => cell.cellId === selectedCellId,
      );
      const current = index >= 0 ? index : 0;
      const next =
        (current + direction + mismatchCells.length) % mismatchCells.length;
      select(mismatchCells[next].cellId);
    },
    [mismatchCells, selectedCellId, select],
  );

  const navigable =
    enabled && !!selectedCell && isDsdEditorSumMismatch(selectedCell);
  useEffect(() => {
    if (!navigable) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      navigate(event.key === "ArrowRight" ? 1 : -1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigable, navigate]);

  return {
    cells,
    mismatchCells,
    selectedCellId,
    selectedCell,
    highlightedCellIds,
    select,
    clear,
    navigate,
  };
};
